import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import logo from '../assets/logo.png';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const navigation = [
    { name: t('nav.home'), href: '/' },
    { name: t('nav.services'), href: '/services' },
    { name: t('nav.consultants'), href: '/consultants' },
    { name: t('nav.about'), href: '/about' },
    { name: t('nav.faq'), href: '/faq' },
    { name: t('nav.contact'), href: '/contact' },
  ];

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setLangMenuOpen(false);
    setIsOpen(false);
  };

  const languages = [
    { code: 'en', label: '🇬🇧 English' },
    { code: 'tc', label: '🇭🇰 繁體中文' },
    { code: 'sc', label: '🇨🇳 简体中文' },
  ];

  return (
    <nav className="bg-navy-900 text-white sticky top-0 z-50 shadow-lg border-b border-gold-600/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-3">
              <img src={logo} alt="Global Link Strategy Limited" className="h-10 sm:h-12 w-auto" />
              <div className="flex flex-col">
                {i18n.language === 'en' || i18n.language === 'en-US' ? (
                  <>
                    <span className="text-sm sm:text-xl font-bold font-serif text-white tracking-wider leading-none">GLOBAL LINK</span>
                    <span className="text-[8px] sm:text-[10px] text-gold-400 tracking-widest leading-none mt-1">STRATEGY LIMITED</span>
                  </>
                ) : (
                  <span className="text-sm sm:text-xl font-bold font-serif text-white tracking-wider leading-none mt-1 sm:mt-2">
                    {i18n.language === 'tc' ? '匯通環球策略有限公司' : '汇通环球策略有限公司'}
                  </span>
                )}
              </div>
            </Link>
          </div>
          
          <div className="hidden md:block">
            <div className="ml-10 flex items-center space-x-8">
              {navigation.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={clsx(
                    'px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200',
                    location.pathname === item.href
                      ? 'text-gold-400'
                      : 'text-gray-300 hover:text-white hover:bg-navy-800'
                  )}
                >
                  {item.name}
                </Link>
              ))}

              {/* Language Switcher Desktop */}
              <div className="relative ml-4">
                <button
                  onClick={() => setLangMenuOpen(!langMenuOpen)}
                  className="flex items-center text-gray-300 hover:text-white focus:outline-none"
                >
                  <Globe className="h-5 w-5 mr-1" />
                  <span className="text-sm font-medium uppercase">{i18n.language === 'en-US' ? 'en' : i18n.language}</span>
                </button>
                
                {langMenuOpen && (
                  <div className="absolute right-0 mt-2 w-40 bg-white rounded-md shadow-lg py-1 ring-1 ring-black ring-opacity-5 focus:outline-none">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className={clsx(
                          "block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100",
                          i18n.language === lang.code && "font-bold text-navy-600"
                        )}
                      >
                        {lang.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="-mr-2 flex md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-navy-800 focus:outline-none"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-navy-900 pb-4 border-t border-navy-800">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {navigation.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setIsOpen(false)}
                className={clsx(
                  'block px-3 py-2 rounded-md text-base font-medium',
                  location.pathname === item.href
                    ? 'text-gold-400 bg-navy-800'
                    : 'text-gray-300 hover:text-white hover:bg-navy-800'
                )}
              >
                {item.name}
              </Link>
            ))}
            
            <div className="border-t border-navy-800 mt-4 pt-4">
              <p className="px-3 text-xs text-gray-500 uppercase tracking-wider mb-2">{t('nav.lang')}</p>
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={clsx(
                    "block w-full text-left px-3 py-2 rounded-md text-base font-medium",
                    i18n.language === lang.code 
                      ? "text-gold-400 bg-navy-800" 
                      : "text-gray-300 hover:text-white hover:bg-navy-800"
                  )}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
